/**
 * Admin Statistics Hooks
 * ადმინისტრატორის სტატისტიკის ჰუკები
 */

import { useQuery } from '@tanstack/react-query';
import {
  getPlatformOverview,
  getUserStatistics,
  getWidgetStatistics,
  getRevenueStatistics,
  getGeminiStatistics,
  getDataFilesStatistics,
  getSystemHealth,
  type StatisticsPeriod,
} from '../features/api/adminStatistics';

export function useSystemHealth() {
  const query = useQuery({
    queryKey: ['admin', 'statistics', 'health'],
    queryFn: getSystemHealth,
    staleTime: 1000 * 60, // 1 minute
    refetchInterval: 1000 * 60, // refetch every minute
  });

  return {
    health: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function usePlatformOverview() {
  const query = useQuery({
    queryKey: ['admin', 'statistics', 'overview'],
    queryFn: getPlatformOverview,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    overview: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useUserStatistics(period: StatisticsPeriod = 'MONTH') {
  const query = useQuery({
    queryKey: ['admin', 'statistics', 'users', period],
    queryFn: () => getUserStatistics(period),
    staleTime: 1000 * 60 * 5,
  });

  return {
    userStats: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useWidgetStatistics(period: StatisticsPeriod = 'MONTH') {
  const query = useQuery({
    queryKey: ['admin', 'statistics', 'widgets', period],
    queryFn: () => getWidgetStatistics(period),
    staleTime: 1000 * 60 * 5,
  });

  return {
    widgetStats: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useRevenueStatistics(period: StatisticsPeriod = 'MONTH') {
  const query = useQuery({
    queryKey: ['admin', 'statistics', 'revenue', period],
    queryFn: () => getRevenueStatistics(period),
    staleTime: 1000 * 60 * 5,
  });

  return {
    revenueStats: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useGeminiStatistics() {
  const query = useQuery({
    queryKey: ['admin', 'statistics', 'gemini'],
    queryFn: getGeminiStatistics,
    staleTime: 1000 * 60 * 5,
  });

  return {
    geminiStats: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useDataFilesStatistics(period: StatisticsPeriod = 'MONTH') {
  const query = useQuery({
    queryKey: ['admin', 'statistics', 'data-files', period],
    queryFn: () => getDataFilesStatistics(period),
    staleTime: 1000 * 60 * 5,
  });

  return {
    dataFilesStats: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
